import { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '../components/AdminLayout';
import {
  getAdminAds, createAd, updateAd, deleteAd, updateAdVisibility, isAdApiUnavailable,
} from '../../../api/adApi';
import { uploadFile } from '../../../api/fileApi';

/**
 * 서버로 보내는 기본 정보 필드.
 * 폼 상태에는 미리보기 전용 값이 섞여 있지 않으므로 그대로 보낸다.
 */
const PAYLOAD_FIELDS = [
  'id', 'advertiser', 'badge', 'title', 'subtitle', 'description',
  'homepageUrl', 'instagramUrl', 'enabled', 'startDate', 'endDate',
  'gridImage', 'bannerImage', 'images',
];

const toPayload = (form) => {
  const out = {};
  PAYLOAD_FIELDS.forEach((k) => {
    const v = form[k];
    out[k] = typeof v === 'string' && v.trim() === '' ? null : v ?? null;
  });
  return out;
};

const EMPTY = {
  id: '', advertiser: '', badge: '', title: '', subtitle: '', description: '',
  homepageUrl: '', instagramUrl: '', enabled: true, startDate: '', endDate: '',
  gridImage: '', bannerImage: '', images: [],
};

function Toggle({ checked, onChange }) {
  return (
    <span
      onClick={() => onChange(!checked)}
      style={{
        display: 'inline-block', width: 36, height: 21, borderRadius: 11, position: 'relative',
        background: checked ? 'var(--color-primary, #090909)' : 'var(--color-border)',
        cursor: 'pointer', flexShrink: 0, verticalAlign: 'middle',
      }}
    >
      <span style={{
        position: 'absolute', top: 2.5, left: checked ? 17 : 2, width: 16, height: 16, borderRadius: '50%',
        background: '#fff', transition: 'left 0.15s ease', boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
      }} />
    </span>
  );
}

function SingleImageField({ label, hint, value, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadFile(file, 'IMAGE');
      onChange(url);
    } catch (err) {
      window.alert(err.message || '이미지 업로드에 실패했습니다.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{ flex: '1 1 220px', minWidth: 200 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 56, height: 56, borderRadius: 8, flexShrink: 0, overflow: 'hidden',
          border: '1px solid var(--color-border)', background: 'var(--color-background)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {value
            ? <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>없음</span>}
        </div>
        <label className="ag-btn ag-btn--sm" style={{ cursor: uploading ? 'default' : 'pointer' }}>
          <i className="ti ti-upload" /> {uploading ? '업로드 중...' : value ? '변경' : '첨부'}
          <input type="file" accept="image/*" style={{ display: 'none' }} disabled={uploading} onChange={handleFile} />
        </label>
        {value && (
          <button type="button" className="ag-btn ag-btn--sm" onClick={() => onChange('')}>제거</button>
        )}
      </div>
      {hint && <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '6px 0 0' }}>{hint}</p>}
    </div>
  );
}

function MultiImageField({ images, setImages }) {
  const fileRef = useRef();

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    const entries = files.map((file) => ({ id: `${Date.now()}-${Math.random()}`, file, uploading: true, url: null }));
    setImages((prev) => [...prev, ...entries]);
    entries.forEach(async (entry) => {
      try {
        const { url } = await uploadFile(entry.file, 'IMAGE');
        setImages((prev) => prev.map((img) => (img.id === entry.id ? { ...img, uploading: false, url } : img)));
      } catch (err) {
        window.alert(err.message || '이미지 업로드에 실패했습니다.');
        setImages((prev) => prev.filter((img) => img.id !== entry.id));
      }
    });
  };

  const removeImage = (id) => setImages((prev) => prev.filter((img) => img.id !== id));

  return (
    <div style={{ flex: '1 1 100%' }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
        상세 페이지 이미지 (여러 장 가능)
      </label>
      <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />
      <button type="button" className="ag-btn ag-btn--sm" onClick={() => fileRef.current?.click()}>
        <i className="ti ti-upload" /> 이미지 업로드
      </button>
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          {images.map((img) => (
            <div key={img.id} style={{ position: 'relative', width: 64, height: 114, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--color-border)', background: '#1a1a1a' }}>
              {img.uploading ? (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 9, color: '#fff' }}>업로드중</span>
                </div>
              ) : (
                <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              )}
              <button
                type="button"
                onClick={() => removeImage(img.id)}
                style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.65)', color: '#fff', cursor: 'pointer', fontSize: 11, lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <p style={{ fontSize: 11, color: 'var(--color-text-tertiary)', margin: '8px 0 0' }}>
        9:16 비율 권장 · 광고 클릭 시 보여지는 상세 페이지에 순서대로 나열됩니다.
      </p>
    </div>
  );
}

function AdForm({ initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState(initial ?? EMPTY);
  const [detailImages, setDetailImages] = useState(() =>
    (initial?.images ?? []).map((url, i) => ({ id: `init-${i}`, url, uploading: false }))
  );
  useEffect(() => {
    setForm(initial ?? EMPTY);
    setDetailImages((initial?.images ?? []).map((url, i) => ({ id: `init-${i}`, url, uploading: false })));
  }, [initial]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const canSubmit = form.id.trim() && form.advertiser.trim() && form.title.trim() &&
    form.startDate && form.endDate && !saving && !detailImages.some((img) => img.uploading);

  const field = (label, key, placeholder, wide = false, required = false, readOnly = false) => (
    <div style={{ flex: wide ? '1 1 100%' : '1 1 220px', minWidth: 200 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
        {label}{required && ' *'}
      </label>
      <input
        className="ag-field__input"
        value={form[key] ?? ''}
        onChange={set(key)}
        placeholder={placeholder}
        readOnly={readOnly}
        title={readOnly ? '광고 주소에 쓰이는 값이라 등록 후에는 바꿀 수 없습니다' : undefined}
        style={{ width: '100%', ...(readOnly ? { background: 'var(--color-bg-muted, #f5f5f7)', cursor: 'not-allowed' } : null) }}
      />
    </div>
  );

  return (
    <form
      className="ag-card"
      style={{ marginBottom: 12 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        onSubmit({ ...form, images: detailImages.map((img) => img.url).filter(Boolean) });
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
        {initial?.id ? '광고 수정' : '광고 등록'}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Toggle checked={form.enabled !== false} onChange={(v) => setForm((prev) => ({ ...prev, enabled: v }))} />
        <span style={{ fontSize: 13 }}>
          {form.enabled !== false ? '노출 중 (기간 안이면 바로 보임)' : '숨김 (기간과 무관하게 항상 숨김)'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {/* id는 광고 상세 주소(/community/ads/{id})가 된다. 바꾸면 밖에 퍼진 링크가 깨지므로 수정 모드에서는 잠근다 */}
        {field('광고 id (영문 슬러그) *', 'id', 'studio-klaxon-2026', false, true, Boolean(initial?.id))}
        {field('광고주명 *', 'advertiser', '예: 스튜디오 클락션', false, true)}
        {field('강조 문구 (카드 상단)', 'badge', '예: 프로필 촬영 이벤트 오픈')}
        {field('제목 *', 'title', '예: 캐스타 X 스튜디오 클락션 50% 할인', false, true)}
        {field('부제 (카드/리스트용)', 'subtitle', '카드에 보여줄 한 줄 소개', true)}
      </div>

      <div style={{ marginTop: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
          상세 소개 문구
        </label>
        <textarea
          className="ag-field__input"
          value={form.description ?? ''}
          onChange={set('description')}
          rows={4}
          placeholder="상세 페이지 본문 (줄바꿈 그대로 반영됩니다)"
          style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit' }}
        />
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
        {field('홈페이지 URL', 'homepageUrl', 'https://...')}
        {field('인스타그램 URL', 'instagramUrl', 'https://instagram.com/...')}
        <div style={{ flex: '1 1 160px', minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
            노출 시작일 *
          </label>
          <input type="date" className="ag-field__input" value={form.startDate ?? ''} onChange={set('startDate')} style={{ width: '100%' }} />
        </div>
        <div style={{ flex: '1 1 160px', minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 6 }}>
            노출 종료일 *
          </label>
          <input type="date" className="ag-field__input" value={form.endDate ?? ''} onChange={set('endDate')} style={{ width: '100%' }} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--color-border)' }}>
        <SingleImageField
          label="배우찾기 / 공고찾기 카드용 이미지"
          hint="1:1 정사각형 권장"
          value={form.gridImage}
          onChange={(url) => setForm((prev) => ({ ...prev, gridImage: url }))}
        />
        <SingleImageField
          label="제작사 대시보드 배너용 이미지"
          hint="3:4 비율 권장"
          value={form.bannerImage}
          onChange={(url) => setForm((prev) => ({ ...prev, bannerImage: url }))}
        />
      </div>

      <div style={{ marginTop: 16 }}>
        <MultiImageField images={detailImages} setImages={setDetailImages} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button className="ag-btn ag-btn--primary" type="submit" disabled={!canSubmit}>
          <i className="ti ti-device-floppy" /> {saving ? '저장 중...' : '저장'}
        </button>
        {onCancel && <button className="ag-btn" type="button" onClick={onCancel}>취소</button>}
      </div>
    </form>
  );
}

export default function AdminAdsPage() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const reqIdRef = useRef(0);

  const fetchAds = useCallback(async () => {
    const myReq = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const list = await getAdminAds();
      if (myReq !== reqIdRef.current) return;
      setAds(list);
      setUnavailable(false);
    } catch (err) {
      if (myReq !== reqIdRef.current) return;
      if (isAdApiUnavailable(err)) { setUnavailable(true); setAds([]); return; }
      const code = err.response?.status;
      setError(code ? `목록을 불러오지 못했습니다. (HTTP ${code})` : (err.message || '목록을 불러오지 못했습니다.'));
      setAds([]);
    } finally {
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAds(); }, [fetchAds]);

  const handleSubmit = async (form) => {
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(form);
      const isNew = !editing;
      if (isNew) await createAd(payload);
      else await updateAd(editing.id, payload);
      setEditing(null);
      fetchAds();
    } catch (err) {
      if (isAdApiUnavailable(err)) {
        window.alert(
          '광고 저장 API가 아직 백엔드에 배포되지 않았습니다.\n' +
          '지금 입력하신 내용을 아래 형태로 개발자에게 전달하면 src/shared/constants/ads.js 에 반영해 배포할 수 있습니다.\n\n' +
          JSON.stringify(toPayload(form), null, 2)
        );
      } else {
        setError(`저장에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ad) => {
    if (!window.confirm(`'${ad.title}' 광고를 삭제할까요?\n되돌릴 수 없습니다.`)) return;
    setBusyId(ad.id);
    try {
      await deleteAd(ad.id);
      fetchAds();
    } catch (err) {
      setError(`삭제에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
    } finally {
      setBusyId(null);
    }
  };

  const handleVisibility = async (ad) => {
    const next = !(ad.enabled !== false);
    setBusyId(ad.id);
    setAds((prev) => prev.map((x) => (x.id === ad.id ? { ...x, enabled: next } : x)));
    try {
      await updateAdVisibility(ad.id, next);
    } catch (err) {
      setAds((prev) => prev.map((x) => (x.id === ad.id ? { ...x, enabled: ad.enabled } : x)));
      if (!isAdApiUnavailable(err)) {
        setError(`노출 설정에 실패했습니다. (HTTP ${err.response?.status ?? '네트워크'})`);
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout pageId="ads">
      <div className="admin-topbar">
        <h1>광고 관리 (Castar Ads)</h1>
        <div className="admin-topbar__right">
          <span style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>총 {ads.length}개</span>
          <span className="admin-badge"><i className="ti ti-user" /> admin</span>
        </div>
      </div>

      <AdForm
        key={editing?.id ?? 'new'}
        initial={editing}
        saving={saving}
        onSubmit={handleSubmit}
        onCancel={editing ? () => setEditing(null) : undefined}
      />

      {error && (
        <div style={{ fontSize: 12, color: 'var(--color-danger)', marginBottom: 10 }}>
          <i className="ti ti-alert-triangle" /> {error}
        </div>
      )}

      {unavailable && (
        <div className="ag-card" style={{ marginBottom: 12, fontSize: 13 }}>
          <i className="ti ti-info-circle" /> 광고 관리 API가 아직 배포되지 않았습니다.
          지금 저장하면 ads.js에 직접 반영할 수 있는 JSON을 안내해드립니다.
        </div>
      )}

      <div className="ag-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="ag-table">
            <thead>
              <tr>{['제목', '광고주', '기간', '노출', '관리'].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="ag-table__empty">불러오는 중...</td></tr>
              ) : ads.length === 0 ? (
                <tr><td colSpan={5} className="ag-table__empty">등록된 광고가 없습니다</td></tr>
              ) : ads.map((ad) => (
                <tr key={ad.id}>
                  <td style={{ fontSize: 13, fontWeight: 500 }}>{ad.title}</td>
                  <td style={{ color: 'var(--color-text-secondary)' }}>{ad.advertiser}</td>
                  <td style={{ color: 'var(--color-text-secondary)', whiteSpace: 'nowrap' }}>
                    {ad.startDate} ~ {ad.endDate}
                  </td>
                  <td>
                    <button
                      className={`ag-badge ${ad.enabled === false ? 'ag-badge--gray' : 'ag-badge--success'}`}
                      style={{ border: 'none', cursor: 'pointer' }}
                      disabled={busyId === ad.id}
                      onClick={() => handleVisibility(ad)}
                    >
                      {ad.enabled === false ? '숨김' : '노출'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="ag-btn ag-btn--sm" onClick={() => setEditing(ad)}>
                        <i className="ti ti-edit" /> 수정
                      </button>
                      <button
                        className="ag-btn ag-btn--sm ag-btn--danger"
                        disabled={busyId === ad.id}
                        onClick={() => handleDelete(ad)}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
